/**
 * สร้าง payload พร้อมเพย์ตามมาตรฐาน EMVCo
 *
 * โครงสร้างเป็น TLV เรียงต่อกัน — แต่ละช่องคือ รหัส 2 หลัก + ความยาว 2 หลัก + ค่า
 * ปิดท้ายด้วยรหัสตรวจสอบ CRC ที่คิดจากทุกอย่างก่อนหน้า รวมหัวของตัวมันเองด้วย
 *
 * ★ QR ของแต่ละบิลระบุจำนวนเงินไว้ในตัว ★
 * ลูกค้าสแกนแล้วยอดขึ้นเองทั้งจำนวน กดโอนได้เลย ไม่ต้องพิมพ์ตัวเลข
 * ซึ่งเป็นจุดที่โอนผิดยอดบ่อยที่สุด และเป็นต้นเหตุที่พนักงานต้องมานั่งเทียบสลิป
 */

/** ตัวชี้ระบบพร้อมเพย์ที่ธนาคารทุกแห่งในไทยใช้ร่วมกัน */
const PROMPTPAY_AID = 'A000000677010111';

function tlv(tag: string, value: string): string {
  return tag + String(value.length).padStart(2, '0') + value;
}

/**
 * CRC-16/CCITT-FALSE — poly 0x1021 · ค่าตั้งต้น 0xFFFF · ไม่กลับบิต
 * มาตรฐาน EMVCo ระบุตัวนี้เจาะจง ไม่ใช่ CRC-16 ตัวอื่นที่ใช้ poly เดียวกัน
 */
export function crc16(text: string): number {
  let crc = 0xffff;
  for (let i = 0; i < text.length; i++) {
    crc ^= text.charCodeAt(i) << 8;
    for (let bit = 0; bit < 8; bit++) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc;
}

export type PromptPayTargetKind = 'mobile' | 'national_id' | 'ewallet';

export interface PromptPayTarget {
  readonly kind: PromptPayTargetKind;
  /** ค่าที่จะใส่ในช่อง — ผ่านการเติมศูนย์ตามรูปแบบแล้ว */
  readonly value: string;
}

/**
 * ตีความเลขพร้อมเพย์ที่เจ้าของร้านกรอกไว้ในข้อมูลสาขา
 *
 * รับได้ทั้งแบบมีขีด มีเว้นวรรค หรือติดกัน เพราะคนกรอกคนละแบบกัน
 *   เบอร์มือถือ 10 หลัก      → 0066 + เบอร์ที่ตัดศูนย์หน้าออก
 *   เลขบัตรประชาชน 13 หลัก  → ใช้ตรง ๆ
 *   กระเป๋าเงิน 15 หลัก      → ใช้ตรง ๆ
 */
export function parseTarget(raw: string): PromptPayTarget {
  const digits = raw.replace(/\D/g, '');

  if (digits.length === 10 && digits.startsWith('0')) {
    return { kind: 'mobile', value: `0066${digits.slice(1)}` };
  }
  if (digits.length === 13) {
    return { kind: 'national_id', value: digits };
  }
  if (digits.length === 15) {
    return { kind: 'ewallet', value: digits };
  }
  throw new Error(
    `เลขพร้อมเพย์ "${raw}" ไม่ถูกต้อง — ต้องเป็นเบอร์มือถือ 10 หลัก ` +
      `เลขบัตรประชาชน 13 หลัก หรือเลขกระเป๋าเงิน 15 หลัก`,
  );
}

const SUBTAG: Record<PromptPayTargetKind, string> = {
  mobile: '01',
  national_id: '02',
  ewallet: '03',
};

/**
 * payload พร้อมสำหรับเข้ารหัสเป็น QR
 *
 * @param promptpayId เลขพร้อมเพย์ของสาขาตามที่บันทึกไว้ในระบบ
 * @param amountSatang จำนวนเงินที่จะให้ขึ้นบนแอปธนาคาร — ไม่ระบุ = ให้ลูกค้ากรอกเอง
 */
export function promptPayPayload(promptpayId: string, amountSatang?: number): string {
  const target = parseTarget(promptpayId);

  let payload =
    tlv('00', '01') +
    // 11 = สแกนซ้ำได้ · 12 = ใช้ครั้งเดียว — QR ที่ระบุยอดของบิลใบหนึ่งเป็นแบบใช้ครั้งเดียว
    tlv('01', amountSatang === undefined ? '11' : '12') +
    tlv('29', tlv('00', PROMPTPAY_AID) + tlv(SUBTAG[target.kind], target.value)) +
    tlv('53', '764') + // สกุลเงินบาท ตาม ISO 4217
    (amountSatang === undefined ? '' : tlv('54', amountText(amountSatang))) +
    tlv('58', 'TH');

  payload += '6304';
  return payload + crc16(payload).toString(16).toUpperCase().padStart(4, '0');
}

/**
 * จำนวนเงินในรูปแบบที่ EMVCo กำหนด — จุดทศนิยม 2 ตำแหน่ง ไม่มีตัวคั่นหลักพัน
 *
 * ★ ห้ามใช้ (satang/100).toFixed(2) ★
 * เลขทศนิยมของ JS ปัดค่าไม่ตรงในบางยอด เช่น 1.005 ได้ "1.00"
 * ยอดบน QR ที่คลาดจากยอดบิลแม้สตางค์เดียว ลูกค้าโอนมาแล้วปิดบิลไม่ได้
 */
function amountText(satang: number): string {
  if (!Number.isInteger(satang) || satang <= 0) {
    throw new Error(`ยอดเงินบน QR ต้องเป็นจำนวนเต็มสตางค์ที่มากกว่า 0 แต่ได้ ${satang}`);
  }
  return `${Math.floor(satang / 100)}.${String(satang % 100).padStart(2, '0')}`;
}
